import { isNil, omitBy } from 'lodash-es';

import parseExerciseFilesForUI from '../_commonParsers/parseExerciseFilesForUI';
import { toExerciseId } from './exercises.branded';
import type {
  ClassicExercise,
  ClassicExerciseSectionNames,
  Exercise,
  ExerciseDto,
  ExerciseListPage,
  ExerciseListRow,
  ExerciseListRowDto,
  ExerciseMarkRequest,
  ExerciseMarkRequestDto,
  ExercisePrint,
  ExercisePrintDto,
  ExerciseTagCategory,
  ExerciseTagCategoryDto,
  ExerciseUserData,
  ExerciseUserDataDto,
  ExerciseUserDataUpdate,
  ExerciseUserDataUpdateDto,
  ExerciseUserMark,
  ExerciseUserMarkDetails,
  ExerciseUserMarkDto,
  SheetsCatalog,
} from './exercises.types';
import type { PaginatedDataDto } from '../_types';

type ParseExercisesForUI = (input: ExerciseDto) => Exercise;
type ParseClassicExerciseForUI = (input: ExerciseDto, names?: ClassicExerciseSectionNames) => ClassicExercise;
type ParseExerciseUserDataForUI = (input: ExerciseUserDataDto) => ExerciseUserData;
type ParseExerciseUserDataUpdateForBE = (input: ExerciseUserDataUpdate) => ExerciseUserDataUpdateDto;
type ParseExerciseMarkRequestForBE = (input: ExerciseMarkRequest) => ExerciseMarkRequestDto;
type ParseExerciseUserMarkDetailsForUI = (input: ExerciseUserMarkDto) => ExerciseUserMarkDetails;

const parseDate = (value: string | null | undefined): Date | null => (value == null ? null : new Date(value));

const parseMarkValue = (mark: ExerciseUserMarkDto['mark']): ExerciseUserMark | null => {
  if (mark === 'easy' || mark === 'hard' || mark === 'explain') {
    return mark;
  }
  return null;
};

const parseExerciseUserDataForUI: ParseExerciseUserDataForUI = raw => ({
  id: raw.id,
  note: raw.note ?? null,
  drawingData: raw.drawing_data ?? null,
  drawingEncodingType: raw.drawing_encoding_type ?? null,
});

const parseExerciseUserDataUpdateForBE: ParseExerciseUserDataUpdateForBE = formData =>
  omitBy(
    {
      note: formData.note,
      drawing_data: formData.drawingData,
      drawing_encoding_type: formData.drawingEncodingType,
    },
    isNil,
  ) as ExerciseUserDataUpdateDto;

const parseExerciseMarkRequestForBE: ParseExerciseMarkRequestForBE = data => ({
  mark: data.mark,
});

const parseExerciseUserMarkDetailsForUI: ParseExerciseUserMarkDetailsForUI = raw => ({
  id: raw.id,
  createdAt: parseDate(raw.created_at),
  mark: parseMarkValue(raw.mark),
  isDone: raw.is_done ?? null,
  updatedAt: parseDate(raw.updated_at),
  exerciseIndex: raw.exercise_index,
  exerciseSectionName: raw.exercise_section_name,
  exerciseSubsectionName: raw.exercise_subsection_name ?? null,
  exerciseSubjectName: raw.exercise_subject_name,
  exerciseUuid: raw.exercise_uuid,
});

const parseExercisesForUI: ParseExercisesForUI = raw => ({
  id: toExerciseId(raw.id ?? raw.uuid),
  subject: raw.subject,
  section: raw.section,
  subsection: raw.subsection,
  tags: raw.tags,
  level: raw.level,
  date: new Date(raw.date),
  source: raw.source,
  files: parseExerciseFilesForUI(raw.files),
  importFileIdentifier: raw.import_file_identifier,
  index: raw.index,
  externalSources: (raw.external_sources ?? []).map(source => ({
    id: source.id,
    category: source.category,
    createdAt: new Date(source.created_at),
    description: source.description,
    index: source.index,
    isMain: source.is_main,
    isSectionRelated: source.is_section_related,
    updatedAt: new Date(source.updated_at),
    url: source.url,
    userRating: source.user_rating,
  })),
  audio: raw.audio,
  video: raw.video,
  answers: raw.answers ?? [],
  globalId: raw.global_id,
  userData: raw.user_data ? parseExerciseUserDataForUI(raw.user_data) : null,
  userMark: parseMarkValue(raw.user_mark?.mark ?? null),
  form: raw.form,
});

const parseClassicExerciseForUI: ParseClassicExerciseForUI = (raw, names = {}) => {
  const mark = parseMarkValue(raw.user_mark?.mark ?? null);
  return {
    id: raw.uuid,
    importFileIdentifier: raw.import_file_identifier,
    audio: raw.audio,
    index: raw.index,
    section: {
      id: raw.section,
      name: names.sectionName || raw.user_mark?.exercise_section_name || '',
    },
    subsection: {
      id: raw.subsection,
      name: names.subsectionName || raw.user_mark?.exercise_subsection_name || '',
    },
    externalSources: (raw.external_sources ?? []).map(source => ({
      id: source.id,
      category: source.category,
      description: source.description,
      url: source.url,
      isMain: source.is_main,
    })),
    userMark: mark && raw.user_mark ? { id: raw.user_mark.id, mark } : null,
    files: (raw.files ?? []).map(file => ({
      croppedPngFile: file.cropped_png_file,
      fileType: file.file_type,
      fileIndex: file.file_index,
    })),
    userData: raw.user_data ? parseExerciseUserDataForUI(raw.user_data) : null,
  };
};

const parseExerciseListRowForUI = (raw: ExerciseListRowDto): ExerciseListRow => ({
  id: raw.uuid,
  uuid: raw.uuid,
  index: raw.index,
  importFileIdentifier: raw.import_file_identifier,
});

const parseExerciseListPageForUI = (raw: PaginatedDataDto<ExerciseListRowDto>): ExerciseListPage => ({
  count: raw.count,
  exercises: (raw.results ?? []).map(parseExerciseListRowForUI),
});

const parseTagCategoriesForUI = (raw: ExerciseTagCategoryDto[]): ExerciseTagCategory[] =>
  (raw ?? []).map(category => ({
    id: category.id,
    name: category.name,
    index: category.index,
    tags: (category.tags ?? []).map(tag => ({
      id: tag.id,
      name: tag.name,
      exercisesCount: tag.exercises_count,
    })),
  }));

const parseExercisePrintForUI = (raw: ExercisePrintDto): ExercisePrint => ({
  id: raw.uuid,
  index: raw.index,
  section: raw.section,
  subsection: raw.subsection,
  files: (raw.files ?? []).map(file => ({
    croppedPngFile: file.cropped_png_file,
    fileType: file.file_type,
  })),
});

const parseSheetsCatalogForUI = (raw: Record<string | number, number[]> | null | undefined): SheetsCatalog => {
  if (!raw || typeof raw !== 'object') return {};
  return Object.fromEntries(Object.entries(raw).map(([year, months]) => [String(year), months ?? []]));
};

export {
  parseExercisesForUI,
  parseClassicExerciseForUI,
  parseExerciseUserDataForUI,
  parseExerciseUserDataUpdateForBE,
  parseExerciseMarkRequestForBE,
  parseExerciseUserMarkDetailsForUI,
  parseExerciseListRowForUI,
  parseExerciseListPageForUI,
  parseTagCategoriesForUI,
  parseExercisePrintForUI,
  parseSheetsCatalogForUI,
};
